import React, { useState, useCallback, useEffect, useContext } from 'react'
import { AuthContext } from '@/layout/Auth'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getOrderDetail, getPaymentUrl, getPurchasedApplications } from '@/api'
import { useQuery } from '@tanstack/react-query'
import { InfoCard, Row, Divider, DetailItem, PayTypeCard } from '../components'
import Text from '@/components/Text'
import { Stack, Skeleton, Grid, FormControlLabel, RadioGroup, Radio, TextField } from '@mui/material'
import { ImageIcon, Copy } from '@/icon'
import { OrderGoodsDetail, PayTypeEnum, PaymentStatusEnum, ApplicationInfo } from '@/types'
import { grayText2, leastText, primary, errorText } from '@/constant'
import alipay_icon from '@/asset/img/alipay_icon.webp'
import voucher_icon from '@/asset/img/voucher_icon.webp'
import Payment from './payment'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import qs from 'qs'
import { encodeUrl } from '@/utils'
import CountDown from './countDownConfirm'
import { Message } from '@cx/ui'

export interface DetailsProps {
  disableBack?: boolean
}

const payTypes = [
  { label: '支付宝扫码支付', value: PayTypeEnum.alipay, icon: alipay_icon },
  { label: '兑换码支付', value: PayTypeEnum.voucher, icon: voucher_icon },
]

const formatGoodsDetails = (detail: string) => {
  const durations = { D: '日', M: '月', Y: '年' }
  let goodsDetail
  try {
    goodsDetail = JSON.parse(detail) as OrderGoodsDetail
  } catch (e) {}
  if (goodsDetail) {
    const details = [
      {
        name: goodsDetail?.package_name,
        value: `${goodsDetail?.duration || ''} ${durations[goodsDetail?.purchase_duration_unit] || ''}`,
      },
    ]
    goodsDetail?.items?.forEach((item) => details.push(item))
    return details?.map((item) => `${item.name} ${item.value}`)
  } else {
    return ['']
  }
}

const Details: React.FC<DetailsProps> = (props) => {
  const { disableBack } = props
  const navigate = useNavigate()
  const { authInfo } = useContext(AuthContext)
  const org_id = authInfo?.orgInfo?.id
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('order_code')
  //enter app after redirected from alipay
  const paidBy = searchParams.get('paid_by')
  const orderOrgId = searchParams.get('org_id')
  const [voucherCode, setVoucherCode] = useState<string>('')
  const [voucherCodeMessage, setMessage] = useState<string>('')
  const [currentPayType, setCurrentPayType] = useState<number>(PayTypeEnum.default)
  const [paymentUrl, setPaymentUrl] = useState<string>()
  const [appName, setAppName] = useState<string>()
  const [app, setApp] = useState<ApplicationInfo>()
  const [openCountDown, setOpenCountDown] = useState<boolean>(false)

  const { data, isLoading } = useQuery(
    ['getOrderDetail', orderId],
    // @ts-ignore
    () => getOrderDetail({ order_code: orderId, org_id: orderOrgId }),
    {
      onSuccess: (res) => {
        setAppName(res?.app_name)
        if (res?.status === PaymentStatusEnum.unpaid) {
          setCurrentPayType(PayTypeEnum.alipay)
        }
      },
      enabled: !!orderId,
      refetchOnWindowFocus: disableBack,
    },
  )

  useQuery(
    ['getPaymentUrl', currentPayType],
    // @ts-ignore
    () => getPaymentUrl({ order_code: orderId, pay_type: currentPayType }),
    {
      enabled: !!orderId && data?.status === PaymentStatusEnum.unpaid,
      onSuccess: (res) => {
        if (currentPayType === PayTypeEnum.voucher) {
          // @ts-ignore
          setPaymentUrl(null)
        } else {
          setPaymentUrl(res?.url)
        }
      },
    },
  )

  useQuery(
    ['getPurchasedApplications', org_id],
    // @ts-ignore
    () => getPurchasedApplications(org_id),
    {
      enabled: !!org_id && !!appName,
      onSuccess: (res) => {
        const currentApp = res?.apps?.find((app) => app.name === appName)
        if (currentApp) {
          setApp(currentApp)
        }
      },
    },
  )

  const handeChangeCurrentPaytype = useCallback((event: React.ChangeEvent, paytype: string) => {
    setCurrentPayType(parseInt(paytype))
  }, [])

  const handleInputVoucherCode = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value
    if (value?.length > 1000) {
      setVoucherCode(value?.slice(0, 1000))
    } else {
      setVoucherCode(value?.trim())
    }
  }, [])

  const onCopy = useCallback(() => {
    Message.success('兑换码已复制到剪贴板')
  }, [])

  const enterApp = useCallback(
    (app: ApplicationInfo) => () => {
      const matchKey = app.url_prefix.split('.')[0].match(/^https?:\/\/(.+)/)
      const target = `${app.url_prefix}?${qs.stringify({ app_id: app.id })}`
      navigate(
        {
          pathname: `/console/app/${matchKey ? matchKey[1] : ''}${encodeUrl(app.url_prefix)}`,
        },
        { state: target },
      )
    },
    [navigate],
  )

  const closeCountdown = useCallback(() => {
    setOpenCountDown(false)
  }, [])

  useEffect(() => {
    // @ts-ignore
    if (parseInt(paidBy) === PayTypeEnum.alipay && app) {
      enterApp(app)()
      // @ts-ignore
    } else if (parseInt(paidBy) === PayTypeEnum.voucher && app) {
      setOpenCountDown(true)
    }
  }, [paidBy, app, enterApp])

  return (
    <InfoCard sx={{ overflow: 'auto' }}>
      <Row sx={{ gap: '32px', alignItems: 'flex-start' }}>
        <ImageIcon
          sx={{
            width: '100px',
            height: '100px',
            minWidth: '100px',
            backgroundColor: isLoading ? '#f9f9f9' : 'transparent',
          }}
          src={data?.logo_url ?? ''}
        />
        <Stack sx={{ gap: '8px' }}>
          {isLoading ? (
            <>
              <Skeleton width={120} height={40} />
              <Skeleton width={100} height={32} />
            </>
          ) : (
            <>
              <Text sx={{ fontSize: '32px' }}>{data?.app_name ?? 'appname'}</Text>
              <Text weight='regular' sx={{ color: grayText2 }}>
                {/*  @ts-ignore */}
                {formatGoodsDetails(data?.goods_detail)?.join(', ') ?? 'app detail'}
              </Text>
            </>
          )}
        </Stack>
      </Row>
      <Divider sx={{ margin: '24px 0' }} />
      <Grid container spacing={2} sx={{ width: '60%' }}>
        {isLoading
          ? [1, 2, 3, 4, 5, 6].map((item, index) => (
              <Grid key={`order-detail-item-${index}`} item xs={6}>
                <Skeleton height={24} width={120} />
              </Grid>
            ))
          : [
              {
                label: '下单时间',
                content: data?.gmt_create,
              },
              {
                label: '订单总额',
                content: data?.total_amount ? `${data?.total_amount} 元` : '',
              },
              { label: '订单编号', content: data?.order_code },
              {
                label: '订单折扣',
                content: data?.discount_amount ? `${data?.discount_amount} 元` : '无',
              },
              { label: '', content: '' },
              data?.pay_type !== PayTypeEnum.voucher && {
                label: '实付金额',
                content: data?.real_amount ? `${data?.real_amount} 元` : '',
              },
            ]?.map((item, index) => (
              <Grid key={`order-detail-item-${index}`} item xs={6}>
                {/*  @ts-ignore */}
                <DetailItem label={item?.label} content={item?.content} />
              </Grid>
            ))}
      </Grid>
      <Text weight='bold' sx={{ marginTop: 'auto' }}>
        支付方式
      </Text>
      <PayTypeCard>
        <RadioGroup
          row
          value={
            data?.status === PaymentStatusEnum.paid || data?.status === PaymentStatusEnum.refunded
              ? data?.pay_type
              : currentPayType
          }
          sx={{ width: '100%', gap: '16px', margin: '0 12px' }}
          onChange={handeChangeCurrentPaytype}
        >
          {payTypes.map((payType, index) => (
            <FormControlLabel
              key={`payment-type-${index}`}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '4px',
                border: '1px solid #D9D9D9',
                padding: '8px',
                flexBasis: '30%',
                transition: 'all 0.2s ease-in-out',
                '& .MuiTypography-root': { width: '100%' },
                ...(data?.status !== PaymentStatusEnum.unpaid
                  ? { borderColor: '#D9D9D9' }
                  : (currentPayType === payType.value || data?.pay_type === payType.value) && {
                      borderColor: primary,
                    }),
              }}
              disabled={data?.status !== PaymentStatusEnum.unpaid}
              value={payType.value}
              label={
                <Text
                  size='xs'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    ...(data?.status !== PaymentStatusEnum.unpaid && {
                      color: leastText,
                    }),
                  }}
                >
                  {payType.label}
                  <ImageIcon sx={{ width: '24px' }} src={payType.icon} />
                </Text>
              }
              control={<Radio />}
            />
          ))}
        </RadioGroup>
        {currentPayType === PayTypeEnum.voucher && (
          <Stack>
            <TextField
              sx={{ backgroundColor: '#fff', fontSize: '14px' }}
              multiline
              value={voucherCode}
              onChange={handleInputVoucherCode}
              maxRows={3}
              minRows={3}
              variant='outlined'
              placeholder='请输入兑换码'
            />
            {voucherCodeMessage && (
              <Text size='xs' sx={{ color: errorText }}>
                {voucherCodeMessage}
              </Text>
            )}
          </Stack>
        )}
        {data?.pay_type === PayTypeEnum.voucher && data?.status === PaymentStatusEnum.paid && (
          <Row
            sx={{
              backgroundColor: '#F7F7F7',
              padding: '12px',
              gap: '24px',
              alignItems: 'flex-start',
            }}
          >
            <Text
              size='xs'
              sx={{
                whiteSpace: 'break-spaces',
                flex: 1,
                lineBreak: 'anywhere',
              }}
            >
              {data?.redeem_code ?? ''}
            </Text>
            <CopyToClipboard text={data?.redeem_code} onCopy={onCopy}>
              <Copy sx={{ width: '14px' }} />
            </CopyToClipboard>
          </Row>
        )}
      </PayTypeCard>

      <Row>
        <Payment
          // @ts-ignore
          orderCode={orderId}
          paymentUrl={paymentUrl ?? window.location.href}
          amount={data?.real_amount ?? 0}
          paid={data?.status === PaymentStatusEnum.paid}
          showTime={data?.status === PaymentStatusEnum.unpaid || data?.status === PaymentStatusEnum.expaired}
          // @ts-ignore
          endTime={data?.expire_time_seconds}
          status={data?.status}
          disableBack={disableBack}
          voucherCode={voucherCode}
          payType={data?.status === PaymentStatusEnum.paid ? data?.pay_type : currentPayType}
          onVoucherCodePaid={setMessage}
        />
      </Row>
      {/*  @ts-ignore */}
      {parseInt(paidBy) === PayTypeEnum.voucher && app && (
        <CountDown
          // @ts-ignore
          appName={appName}
          time={5}
          open={openCountDown}
          onClose={closeCountdown}
          onConfirm={enterApp(app)}
        />
      )}
    </InfoCard>
  )
}

export default Details
